import { StrictMode } from "react"
import type { ErrorInfo, FC, ReactElement } from "react"

import { reactErrorHandler as sentryReactErrorHandler, withProfiler as withSentryProfiler } from "@sentry/react"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
// eslint-disable-next-line import/no-extraneous-dependencies
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import { type Root, createRoot } from "react-dom/client"
import { Provider as ReduxProvider } from "react-redux"
// is stable in react-router-dom v7+
import { unstable_HistoryRouter as HistoryRouter } from "react-router-dom"
import { useMount } from "react-use"

import "$/i18n/i18n"
import { isAccessTokenValid, isRefreshTokenValid } from "$/utils/tokens"

import { createQueryClient, enableDevelopmentMocks } from "@/3514/clients"
import { Main, RootErrorBoundary } from "@/3514/components"
import { EProductTypeQueryKey, EUserQueryKey } from "@/3514/config"
import { browserHistory } from "@/3514/constants"
import { productTypeFetcher, userQueryFetcher } from "@/3514/fetchers"
import { store as reduxStore } from "@/3514/store"
import { captureSentryRootError, hours, initDatadog, initSentry, loadSentryIntegrations } from "@/3514/utils"
import "@/assets/main-app.scss"
import "@/assets/tailwind.css"
import { ROOT_ELEMENT_SELECTOR } from "@/constants"

const client: QueryClient = createQueryClient()

const pageContainer: HTMLDivElement = document.getElementById(ROOT_ELEMENT_SELECTOR) as HTMLDivElement
const root: Root = createRoot(pageContainer, {
    onUncaughtError: sentryReactErrorHandler((error: Error, errorInfo: ErrorInfo): void =>
        captureSentryRootError(error, errorInfo)
    ),
    onRecoverableError: sentryReactErrorHandler()
})

const MainContainerWithProfiler: FC = withSentryProfiler(Main)

initSentry()
initDatadog()

const MainApp: FC = (): ReactElement => {
    useMount((): void => void enableDevelopmentMocks())

    return (
        <ReduxProvider store={reduxStore}>
            <RootErrorBoundary>
                <QueryClientProvider client={client}>
                    <ReactQueryDevtools />

                    <HistoryRouter history={browserHistory as never}>
                        <MainContainerWithProfiler />
                    </HistoryRouter>
                </QueryClientProvider>
            </RootErrorBoundary>
        </ReduxProvider>
    )
}

async function bootstrap(): Promise<void> {
    await loadSentryIntegrations()

    if (isAccessTokenValid() && isRefreshTokenValid())
        await client
            .prefetchQuery({
                queryKey: EUserQueryKey(),
                queryFn: userQueryFetcher,
                staleTime: hours(1),
                gcTime: hours(1)
            })
            .then(
                async (): Promise<void> =>
                    await client.prefetchQuery({
                        queryKey: EProductTypeQueryKey(),
                        queryFn: productTypeFetcher,
                        staleTime: hours(2),
                        gcTime: hours(2),
                        retry: false
                    })
            )

    if (pageContainer)
        root.render(
            <StrictMode>
                <MainApp />
            </StrictMode>
        )
}

bootstrap()
